<template>
  <div class="pb-4">
    <p class="text-text-neutral-tertiary text-xs font-medium">
      {{ t('cashbackInfo.how-cashback-works') }}
    </p>
    <div class="mt-3 flex items-center gap-3">
      <CashbackInfoLevelStarIcon :level-name="activeLevelName" />
      <div class="flex flex-1 flex-col gap-0.5">
        <p class="text-text-neutral-primary font-medium">{{ levelTitle }}</p>
        <p
          v-if="!isPromoApplicable"
          class="text-text-neutral-tertiary text-sm font-medium"
        >
          {{ t('cashbackInfo.partner-cashback') }}
        </p>
        <button
          type="button"
          class="text-text-positive-primary flex items-center gap-0.5"
          @click="toggleExpanded"
        >
          <span class="text-sm font-medium">{{ toggleLabel }}</span>
          <ChevronDownSmIcon
            class="transition-transform duration-300"
            :class="{ 'rotate-180': isExpanded }"
          />
        </button>
      </div>
      <div
        class="rounded-full bg-gradient-to-r from-[#036BB9] to-[#00C57F] px-2 py-1 text-sm font-semibold text-white"
      >
        {{ displayCashback }}%
      </div>
    </div>
    <WithAccordionFadeTransition :is-expanded="isExpanded">
      <div :class="{ 'pb-3': isPromoApplicable }">
        <div
          ref="scrollContainerEl"
          class="no-scrollbar -mx-4 mt-3 flex gap-1 overflow-x-auto px-4 pb-2"
        >
          <CashbackInfoLevelCard
            v-for="level in cashbackLevels"
            :key="level.name"
            :level="level"
            :is-active="isLevelActive(level)"
          />
        </div>
        <div
          v-if="showPromoRow"
          class="level-card-ring relative mt-2 rounded-[22px] p-[2px]"
        >
          <div
            class="promo-offer-badge text-2xs absolute -bottom-[9px] left-1/2 -translate-x-1/2 rounded-[20px] px-1.5 py-0.5 font-medium whitespace-nowrap text-white"
          >
            {{ t('cashbackInfo.promo-applicable-offer') }}
          </div>
          <div
            class="promo-row-inner-bg bg-background-neutral-secondary flex items-center gap-3 rounded-[20px] px-4 py-3"
          >
            <img
              :src="promoIcon"
              width="32"
              height="32"
              alt=""
              class="icon-shadow shrink-0 rounded-full"
            />
            <div class="flex items-baseline gap-1">
              <span
                class="text-text-neutral-primary text-sm font-medium opacity-90"
              >
                {{ t('cashbackInfo.promo-applicable-title') }}
              </span>
              <span
                class="freedom-loyalty-text inline-block text-base font-bold"
              >
                {{ displayCashback }}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </WithAccordionFadeTransition>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { WithAccordionFadeTransition } from '../../../shared/ui';
  import type { CashbackLevel } from '../types';
  import ChevronDownSmIcon from '../assets/16/chevron-down.svg?component';
  import CashbackInfoLevelCard from './CashbackInfoLevelCard.vue';
  import CashbackInfoLevelStarIcon from './CashbackInfoLevelStarIcon.vue';
  import messages from '../locales.json';

  const props = defineProps<{
    cashbackLevels: CashbackLevel[];
    isPromoApplicable?: boolean;
  }>();

  const { t } = useI18n({ messages });
  const isExpanded = ref(false);
  const isAnimating = ref(false);
  const scrollContainerEl = useTemplateRef('scrollContainerEl');

  const toggleExpanded = () => {
    if (isAnimating.value) {
      return;
    }
    isExpanded.value = !isExpanded.value;
    isAnimating.value = true;
    // Lock matches the longest transition in WithAccordionFadeTransition (opacity 0.5s).
    setTimeout(() => {
      isAnimating.value = false;
    }, 500);
  };

  watch(isExpanded, async value => {
    if (!value) {
      return;
    }
    await nextTick();
    const container = scrollContainerEl.value;
    if (!container) {
      return;
    }
    // The active card carries the .level-card-ring class — use it as a selector
    // instead of a tracked ref so we don't need to expose DOM elements from the child.
    const activeCard = container.querySelector(
      '.level-card-ring',
    ) as HTMLElement | null;
    if (!activeCard) {
      return;
    }
    const itemLeft = activeCard.offsetLeft - container.offsetLeft;
    const itemRight = itemLeft + activeCard.offsetWidth;
    if (itemLeft < container.scrollLeft) {
      container.scrollLeft = itemLeft;
    } else if (itemRight > container.scrollLeft + container.clientWidth) {
      container.scrollLeft = itemRight - container.clientWidth;
    }
  });

  const isLevelActive = (level: CashbackLevel) =>
    level.isActive && !props.isPromoApplicable;

  const showPromoRow = computed(
    () => props.isPromoApplicable && !!promoIcon.value,
  );

  const displayCashback = computed(() => {
    if (props.isPromoApplicable) {
      return Math.max(0, ...props.cashbackLevels.map(l => l.cashback));
    }
    return props.cashbackLevels.find(l => l.isActive)?.cashback ?? 0;
  });

  const promoIcon = computed(
    () => props.cashbackLevels[props.cashbackLevels.length - 1]?.icon ?? '',
  );

  const levelTitle = computed(() =>
    props.isPromoApplicable
      ? t('cashbackInfo.promo-applicable-title')
      : t('cashbackInfo.your-level', { level: activeLevelName.value }),
  );

  const toggleLabel = computed(() =>
    isExpanded.value ? t('cashbackInfo.hide-all') : t('cashbackInfo.show-all'),
  );

  const activeLevelName = computed(() => {
    if (props.isPromoApplicable) {
      return 'promo';
    }
    return props.cashbackLevels.find(l => l.isActive)?.name ?? 'Standard';
  });
</script>

<style scoped>
  .level-card-ring {
    background: linear-gradient(to bottom right, #036bb9, #00c57f);
  }

  .promo-offer-badge {
    background-image: linear-gradient(
      220.72deg,
      rgb(0, 197, 127) 0%,
      rgb(3, 107, 185) 100%
    );
  }

  .promo-row-inner-bg {
    background-image: linear-gradient(
      -16.02deg,
      rgba(163, 210, 186, 0.4) 0.19%,
      rgb(var(--background-neutral-secondary) / 0.4) 100%
    );
  }

  .icon-shadow {
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.05);
  }
</style>
